import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/shared/lib/utils';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

interface PopoverContentProps extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  /**
   * Radix portals PopoverContent to `document.body` by default, which
   * escapes ancestor overflow clipping — but nested inside a Radix `Dialog`,
   * the portaled content ends up outside the Dialog's own focus-trap/layer
   * boundary: clicking an item inside closes the popover (the Dialog's
   * dismissable-layer intercepts the pointerdown) WITHOUT the item's
   * onClick ever firing. Set `portal={false}` for any Popover nested inside
   * a FormDialog/Dialog (confirmed via WastePicker/WasteCodePicker repro,
   * 2026-07-18) — positioning still works via Radix Popper, just without
   * the portal.
   */
  portal?: boolean;
}

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = 'start', sideOffset = 4, portal = true, ...props }, ref) => {
  const content = (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      // PLAN.md item 4 — scale from the trigger (Radix's own transform-origin
      // var), not the content's center: today `zoom-in-95` scales from the
      // middle regardless of which edge/corner the popover actually opened
      // from.
      style={{ transformOrigin: 'var(--radix-popover-content-transform-origin)' }}
      className={cn(
        'z-50 w-72 rounded-xl border bg-popover p-2 text-popover-foreground shadow-md outline-none duration-popover data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className,
      )}
      {...props}
    />
  );
  return portal ? <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal> : content;
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
